// Demo Firehose Component
// Looks exactly like normal firehose widgets, just with demo data
// Uses same Virtuoso + FirehoseHighlightItem pattern

import { useRef, useEffect, useState, useMemo } from "react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { ClockIcon } from "@heroicons/react/24/outline";
import { FirehoseHighlightItem } from "@/widget-components/firehose-widgets/firehose-all/components/FirehoseHighlightItem";
import EmptyPlaceholder from "@/ui-components/empty-placeholder";
import { LoadMoreButton } from "@/ui-components/load-more-button";
import { useDemoFeed } from "@/shared/api/demo-firehose/useDemoFeed";
import { useDemoHeaderFilterMenu } from "@/widget-components/firehose-widgets/shared/hooks/useDemoHeaderFilterMenu";
import { PricingModal } from "@/ui-components/PricingModal";
import {
  DemoSource,
  DEMO_SOURCES,
  DEMO_WATCHLIST_TICKERS,
  WIDGET_TO_DEMO_SOURCES,
} from "@/shared/api/demo-firehose";
import { getDemoWatchlistNews } from "@/shared/api/demo-firehose/demoClient";
import { NewsFeedItem } from "@/shared/api/firehose/models";
import { Portfolio } from "@/types/Portfolio";
import { emptyNotifications } from "@/types/notifications";
import { WidgetHeaderOptions } from "@/widget-components/types";
import { getWidgetById } from "@/utils/widgets";

interface DemoFirehoseProps {
  /** Widget ID to determine which sources to show */
  widgetId?: string;
  /** Header options from WidgetContainer */
  headerOptions?: WidgetHeaderOptions;
}

// Hardcoded Mag 7 portfolio for highlighting
const MAG7_PORTFOLIO: Portfolio = {
  id: -1, // Special ID for demo
  name: "Magnificent 7",
  color: "#3B82F6", // Blue
  tickers: [...DEMO_WATCHLIST_TICKERS],
  keywords: [],
  priority: 0,
  notifications: emptyNotifications(),
  customizations: { includeRelatedHeadlines: false },
  owner_type: "NEWSQUAWK",
};

/**
 * Check if a story matches the Magnificent 7 watchlist
 */
function matchesMag7Watchlist(story: NewsFeedItem): boolean {
  const text = `${story.title || ""} ${story.body || ""}`.toUpperCase();

  // Check if any ticker appears in the text
  if (DEMO_WATCHLIST_TICKERS.some((ticker) => text.includes(ticker))) {
    return true;
  }

  // Also check the tickers field if available
  if (story.tickers) {
    const storyTickers = Array.isArray(story.tickers)
      ? story.tickers
      : String(story.tickers).split(/[,;]/);

    return storyTickers.some((ticker) =>
      DEMO_WATCHLIST_TICKERS.some(
        (watchlistTicker) => ticker.toUpperCase().trim() === watchlistTicker
      )
    );
  }

  return false;
}

export function DemoFirehose({
  widgetId = "FirehoseAll",
  headerOptions,
}: DemoFirehoseProps) {
  const virtuosoHandle = useRef<VirtuosoHandle>(null);
  const [isPricingOpen, setIsPricingOpen] = useState(false);

  // Get allowed sources for this widget
  const allowedSources: readonly DemoSource[] =
    WIDGET_TO_DEMO_SOURCES[widgetId] || DEMO_SOURCES;

  // Fetch demo data for this widget
  const { stories, isLoading, hasMore, loadNext } = useDemoFeed({
    sources: allowedSources,
  });

  // Set title with "- Demo" suffix
  useEffect(() => {
    if (headerOptions?.setTitle) {
      const widget = getWidgetById(widgetId);
      const baseName = widget?.name || "Firehose";
      headerOptions.setTitle(`${baseName} - Demo`);
    }
  }, [widgetId, headerOptions]);

  // Watchlist stories from /news-demo/watchlist endpoint
  const [watchlistStories, setWatchlistStories] = useState<NewsFeedItem[]>([]);
  const [watchlistLoading, setWatchlistLoading] = useState(false);

  // Create filter menu with Mag 7 option
  const [filterSelections] = useDemoHeaderFilterMenu({
    pushAction: headerOptions?.pushAction,
    persistKey: `demo-${widgetId}`,
  });

  // Check if Mag 7 filter is active
  const isMag7FilterActive =
    filterSelections["demo-watchlist-filter"]?.includes("mag7");
  const isMag7HighlightActive =
    filterSelections["demo-watchlist-highlight"]?.includes("mag7");

  // Fetch watchlist stories when filter is activated
  useEffect(() => {
    if (isMag7FilterActive && watchlistStories.length === 0 && !watchlistLoading) {
      setWatchlistLoading(true);
      getDemoWatchlistNews()
        .then((data) => {
          // Filter by allowed sources for this widget
          const filtered = data.filter((story) =>
            allowedSources.includes(story.backend_source as DemoSource)
          );
          setWatchlistStories(filtered);
        })
        .catch((error) => {
          console.error("Failed to fetch watchlist stories:", error);
        })
        .finally(() => {
          setWatchlistLoading(false);
        });
    }
  }, [isMag7FilterActive, watchlistStories.length, watchlistLoading, allowedSources]);

  // Merge and dedupe stories
  const mergedStories = useMemo(() => {
    if (isMag7FilterActive) {
      // When filter is active, show only watchlist stories
      // Merge with main stories to catch any SSE updates that match
      const watchlistIds = new Set(watchlistStories.map((s) => s.id));
      const matchingMainStories = stories.filter(
        (s) => matchesMag7Watchlist(s) && !watchlistIds.has(s.id)
      );
      const allWatchlist = [...matchingMainStories, ...watchlistStories];
      // Sort by ID descending (most recent first)
      return allWatchlist.sort((a, b) => b.id - a.id);
    }
    return stories;
  }, [stories, watchlistStories, isMag7FilterActive]);

  // Map stories to include portfolio highlight info
  const storiesWithHighlight = useMemo((): [NewsFeedItem, Portfolio | undefined][] => {
    return mergedStories.map((story) => {
      if (isMag7HighlightActive && matchesMag7Watchlist(story)) {
        return [story, MAG7_PORTFOLIO];
      }
      return [story, undefined];
    });
  }, [mergedStories, isMag7HighlightActive]);

  const showLoadMore = storiesWithHighlight.length > 0;
  const showLoading = isLoading || watchlistLoading;

  return (
    <>
      <PricingModal isOpen={isPricingOpen} onClose={() => setIsPricingOpen(false)} />
      <div className="h-full flex flex-col relative">
      <Virtuoso
        ref={virtuosoHandle}
        className="flex-1 scrollbar-thin"
        totalCount={storiesWithHighlight.length}
        data={storiesWithHighlight}
        components={{
          Header: () => (
            <div className="flex items-center justify-center gap-3 bg-gray-800/80 border-b border-gray-700 py-2 px-3">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <ClockIcon className="h-3.5 w-3.5 text-terminal-orange" />
                <span>
                  <span className="text-terminal-orange font-medium">24h delayed</span>
                </span>
              </div>
              <button
                onClick={() => setIsPricingOpen(true)}
                className="px-2.5 py-0.5 text-xs font-medium text-white bg-squawk-blue hover:bg-squawk-blue/80 rounded-md transition-colors cursor-pointer"
              >
                Get Live Data
              </button>
            </div>
          ),
          EmptyPlaceholder: () => (
            <EmptyPlaceholder
              message={
                isMag7FilterActive
                  ? "No stories match the Magnificent 7 watchlist"
                  : "No demo stories available"
              }
              isLoading={showLoading}
            />
          ),
          Footer: () =>
            showLoadMore ? (
              hasMore ? (
                <LoadMoreButton loading={isLoading} loadNextPage={loadNext} />
              ) : (
                <div className="flex justify-center py-4 text-gray-400 text-sm">
                  End of results
                </div>
              )
            ) : null,
        }}
        itemContent={(_, [data, portfolio]) => {
          return (
            <FirehoseHighlightItem
              item={data}
              key={data.id}
              highlightInfo={
                portfolio
                  ? {
                      color: portfolio.color,
                      matchedTickers: [...(portfolio.tickers || [])],
                      portfolioId: portfolio.id,
                      portfolioName: portfolio.name,
                    }
                  : undefined
              }
            />
          );
        }}
      />
    </div>
    </>
  );
}

export default DemoFirehose;
